import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import {
  LayoutDashboard, Users, Briefcase, Star, BadgeCheck, Shield, BarChart3,
  Link2, Search, Settings, UserCircle, ChevronDown, Menu, X, Sun, Moon, LogOut
} from "lucide-react";
import { getTheme, fonts } from "../theme";
import Logo from "../components/Logo";
import { SUPER_ADMIN_NAV, SUPER_ADMIN_ACCOUNT_NAV, isNavActive, isGroupActive } from "./nav";

const ICONS = {
  home: LayoutDashboard,
  users: Users,
  briefcase: Briefcase,
  star: Star,
  check: BadgeCheck,
  shield: Shield,
  chart: BarChart3,
  link: Link2,
  search: Search,
  gear: Settings,
  user: UserCircle,
};

function NavItem({ item, pathname, theme, onLogout, onNavigate }) {
  const Icon = ICONS[item.icon] || LayoutDashboard;
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const groupActive = isGroupActive(pathname, item);
  const [open, setOpen] = useState(groupActive);
  React.useEffect(() => {
    if (groupActive) setOpen(true);
  }, [groupActive]);

  if (!hasChildren) {
    return (
      <NavLink
        to={item.to}
        end
        onClick={onNavigate}
        className="d-flex align-items-center gap-2 text-decoration-none px-3 py-2 mb-1"
        style={{
          borderRadius: 10,
          color: isNavActive(pathname, item.to) ? "#fff" : theme.text,
          background: isNavActive(pathname, item.to) ? theme.accent : "transparent",
          fontWeight: 650,
          fontSize: 13,
        }}
      >
        <Icon size={16} /> {item.label}
      </NavLink>
    );
  }

  return (
    <div className="mb-1">
      <button
        type="button"
        className="w-100 d-flex align-items-center justify-content-between border-0 px-3 py-2"
        onClick={() => setOpen((v) => !v)}
        style={{
          background: groupActive ? theme.accentDim : "transparent",
          color: theme.text,
          borderRadius: 10,
          fontWeight: 700,
          fontSize: 13,
        }}
      >
        <span className="d-flex align-items-center gap-2"><Icon size={16} /> {item.label}</span>
        <ChevronDown size={14} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
      </button>
      {open ? (
        <div className="ps-3 pb-1">
          {item.children.map((child) => {
            if (child.to === "__logout__") {
              return (
                <button
                  key={child.label}
                  type="button"
                  className="w-100 text-start border-0 px-3 py-2 d-flex align-items-center gap-2"
                  onClick={onLogout}
                  style={{ background: "transparent", color: theme.textMute, borderRadius: 8, fontSize: 13 }}
                >
                  <LogOut size={14} /> {child.label}
                </button>
              );
            }
            return (
              <Link
                key={child.to}
                to={child.to}
                onClick={onNavigate}
                className="d-block text-decoration-none px-3 py-2"
                style={{
                  borderRadius: 8,
                  fontSize: 13,
                  color: isNavActive(pathname, child.to) ? "#fff" : theme.textMute,
                  background: isNavActive(pathname, child.to) ? theme.accent : "transparent",
                  fontWeight: isNavActive(pathname, child.to) ? 700 : 500,
                }}
              >
                {child.label}
              </Link>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default function SuperAdminLayout({ user, onLogout, themeMode = "dark", setThemeMode }) {
  const theme = getTheme("super_admin", themeMode);
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(true);
  const ctx = useMemo(() => ({ user, onLogout, theme, themeMode, setThemeMode }), [user, onLogout, theme, themeMode, setThemeMode]);

  useEffect(() => {
    if (location.pathname.length > 1 && location.pathname.endsWith("/")) {
      navigate(location.pathname.replace(/\/+$/, "") + location.search, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  const sidebar = (
    <div className="d-flex flex-column h-100" style={{ minHeight: "100%" }}>
      <div className="px-3 pt-3 pb-2">
        <div className="d-flex align-items-center justify-content-between gap-2">
          <Logo color={theme.accent} size={18} subtitle={false} />
          <button type="button" className="btn btn-sm d-md-none border-0" style={{ color: theme.text }} onClick={() => setMobileOpen(false)}><X size={18} /></button>
        </div>
        <div className="small fw-bold text-uppercase mt-2" style={{ color: "#F59E0B", letterSpacing: "0.12em" }}>Super Admin</div>
      </div>
      <div className="flex-grow-1 overflow-auto px-2 pb-3">
        {SUPER_ADMIN_NAV.map((item) => (
          <NavItem key={item.key} item={item} pathname={location.pathname} theme={theme} onLogout={onLogout} onNavigate={() => setMobileOpen(false)} />
        ))}
      </div>
      <div className="px-2 pb-3 border-top pt-2" style={{ borderColor: theme.line }}>
        <NavItem item={SUPER_ADMIN_ACCOUNT_NAV} pathname={location.pathname} theme={theme} onLogout={onLogout} onNavigate={() => setMobileOpen(false)} />
      </div>
    </div>
  );

  return (
    <div className="vh-100 d-flex overflow-hidden" style={{ background: theme.bg, color: theme.text, fontFamily: fonts.body }}>
      <aside className="d-none d-md-flex flex-column h-100" style={{ width: 260, flexShrink: 0, background: theme.navBg, borderRight: `1px solid ${theme.line}`, position: "sticky", top: 0 }}>
        {sidebar}
      </aside>
      {mobileOpen ? (
        <div className="d-md-none position-fixed top-0 start-0 w-100 h-100" style={{ zIndex: 1040, background: "rgba(0,0,0,0.45)" }} onClick={() => setMobileOpen(false)}>
          <div className="h-100" style={{ width: 260, maxWidth: "86vw", background: theme.canvas }} onClick={(e) => e.stopPropagation()}>
            {sidebar}
          </div>
        </div>
      ) : null}
      <div className="flex-grow-1 d-flex flex-column h-100 overflow-hidden" style={{ minWidth: 0 }}>
        <header className="d-flex align-items-center justify-content-between gap-2 px-3 py-1 flex-shrink-0" style={{ background: theme.navBg, borderBottom: `1px solid ${theme.line}`, minHeight: 42 }}>
          <button type="button" className="btn btn-sm d-md-none d-flex align-items-center gap-1" style={{ color: "#fff", background: theme.accent }} onClick={() => setMobileOpen(true)}>
            <Menu size={16} /> Menu
          </button>
          <div className="small fw-semibold" style={{ color: theme.textMute }}>{user?.name || "Super Admin"}</div>
          <button
            type="button"
            className="btn btn-sm border-0"
            style={{ color: theme.text }}
            onClick={() => setThemeMode && setThemeMode(themeMode === "dark" ? "light" : "dark")}
            title="Toggle theme"
          >
            {themeMode === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </header>
        <main className="flex-grow-1 p-2 p-md-2.5 d-flex flex-column overflow-auto" style={{ minHeight: 0 }}>
          <Outlet context={ctx} />
        </main>
      </div>
    </div>
  );
}

export function useSuperAdmin() {
  return useOutletContext();
}
